import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dispatch, SetStateAction } from "react";
import { useTranslations } from "next-intl";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  tableName: string;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  currentPage: number;
  totalPages: number;
  perPage: number;
  setPerPage: Dispatch<SetStateAction<number>>;
}

export function DataTablePagination<TData>({
  table,
  setCurrentPage,
  currentPage,
  totalPages,
  tableName,
  perPage,
  setPerPage,
}: DataTablePaginationProps<TData>) {
  const variants = [5, 20, 50];
  const table_elements = useTranslations('table_elements');

  return (
    <div className="flex font-sans gap-2 items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {table.getFilteredSelectedRowModel().rows.length} {table_elements('of')}{" "}
        {table.getFilteredRowModel().rows.length} {table_elements('rows_selected')}.
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">{table_elements('rows_per_page')}e</p>          <Select
            value={`${perPage}`}
            onValueChange={(value) => {
              setPerPage(Number(value));
              if (typeof window !== 'undefined') {
                localStorage.setItem(`perPage-${tableName}`, Number(value).toString());
              }
              setCurrentPage(1);
              table.setPageIndex(0);
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {variants.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex  items-center justify-center text-sm font-medium">
          {table_elements('page')} {currentPage} {table_elements('of')} {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              const firstPage = 1;
              setCurrentPage(firstPage);
              table.setPageIndex(firstPage - 1);
            }}
            disabled={currentPage <= 1}
          >
            <DoubleArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            disabled={currentPage <= 1}
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              const prevPage = currentPage - 1;
              setCurrentPage(prevPage);
              table.setPageIndex(prevPage + 1);
            }}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            disabled={currentPage >= totalPages}
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              const nextPage = currentPage + 1;
              setCurrentPage(nextPage);
              table.setPageIndex(nextPage - 1);
            }}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              const lastPage = totalPages;
              setCurrentPage(lastPage);
              table.setPageIndex(lastPage - 1);
            }}
            disabled={currentPage >= totalPages}
          >
            <DoubleArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
